import type { Prettify } from '#types/prettify.type';

/**
 * Creates a shallow copy of the given object with specified keys omitted.
 *
 * @typeParam TObject - The type of the source object.
 * @typeParam TKey - The union of keys to omit from the source object.
 * @param value - The source object to omit keys from.
 * @param keys - The keys to omit from the resulting object.
 * @returns A new object with the specified keys omitted.
 *
 * @example
 * ```typescript
 * const obj = { a: 1, b: 2, c: 3 };
 * const result = omit(obj, 'b', 'c');
 * // result is { a: 1 }
 * ```
 */
export const omit = <TObject extends Record<string, unknown>, TKey extends keyof TObject>(
  value: TObject,
  ...keys: readonly TKey[]
) => {
  const map = new Map<TKey, TObject[TKey]>(Object.entries(value) as [TKey, TObject[TKey]][]);

  for (const key of keys) {
    if (map.has(key)) {
      map.delete(key);
    }
  }

  return {
    ...Object.fromEntries(map),
  } as unknown as Prettify<Omit<TObject, TKey>>;
};
